import { AccountType, IAppModeValue } from '@logz-pkg/enums';
import { accountService, configProvider } from '@logz-pkg/frontend-services';
import { welcomeRouteState } from '../../../app/states/welcome.routes';
import { AppModes } from './app-modes.factory';
import { appRouter } from 'services/router/router.service';
import { landingPageAppStateService } from 'ui/state/landing-page/landing-page.app-state.service';
import { welcomeStateService } from 'ui/state/welcome.state.service';
import { getIsNewAppEnabled } from 'ui/hooks/is-new-application-enabled.hook';

export const ACCOUNT_TYPE_TO_MODE: Partial<Record<AccountType, IAppModeValue>> = {
  [AccountType.Owner]: AppModes.OPERATIONS,
  [AccountType.Sub]: AppModes.OPERATIONS,
  [AccountType.Security]: AppModes.SECURITY,
  [AccountType.Metrics]: AppModes.OPERATIONS,
  [AccountType.Tracing]: AppModes.OPERATIONS,
  [AccountType.Restore]: AppModes.OPERATIONS,
};

const waitForever = () => new Promise(resolve => setTimeout(resolve, Number.MAX_SAFE_INTEGER));

export const redirectToNewAppIfNeeded = async () => {
  const { isEnabled } = await getIsNewAppEnabled();

  if (isEnabled) {
    window.location.replace('/360/');
    // Make sure App will never be ready before redirecting
    await waitForever();
  }
};

const isSecurityMode = async () => {
  const mode = await getCurrentMode();

  return mode === AppModes.SECURITY;
};

const isOperationsMode = async () => {
  const mode = await getCurrentMode();

  return mode === AppModes.OPERATIONS;
};

const getCurrentMode = (): Promise<Partial<IAppModeValue>> =>
  accountService
    .getAccountType()
    .then(accountType => {
      if (!accountType) return {};

      return ACCOUNT_TYPE_TO_MODE[accountType] || {};
    })
    .catch(() => ({}));

const getModeRootState = async () => {
  const mode = await getCurrentMode();
  let rootState: string = '';

  if (mode && mode.rootState) {
    ({ rootState } = mode);

    if (await welcomeStateService.shouldGoToWelcomePage()) {
      rootState = welcomeRouteState;
    } else if (mode === AppModes.OPERATIONS) {
      rootState = await configProvider.getValue('homepage');
    }
  }

  return rootState;
};

/**
 * Go to root state by Mode, in case of Operations mode open welcome flow if required.
 * @returns {Promise<string>} - Current new state
 */
const goToModeRootState = async (stateParams = {}, stateOptions = {}) => {
  await redirectToNewAppIfNeeded();

  const rootState = await getModeRootState();

  const landingPage = await landingPageAppStateService.get();

  if (landingPage?.state?.name) {
    const { name, params } = landingPage.state;

    appRouter.stateService.go(name, params, { inherit: false });
  } else if (landingPage?.link) {
    appRouter.urlService.url(landingPage.link);
  } else {
    appRouter.stateService.go(rootState, stateParams, stateOptions);
  }
};

const goToModeKibanaState = async (stateParams = {}) => {
  const mode = await getCurrentMode();

  if (!mode.kibanaState) return;

  await appRouter.stateService.go(mode.kibanaState, stateParams);
};

export const appModeService = {
  isSecurityMode,
  isOperationsMode,
  getCurrentMode,
  goToModeRootState,
  getModeRootState,
  goToModeKibanaState,
};
